import React, { useMemo } from 'react';
import { Tag, Tooltip } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/config';
import { useAuth } from '../../context/AuthContext';

/**
 * Satış Süreci ekranlarının ortak parçaları (26.10.2026) — routes/satis_sureci.py (/satis-sureci).
 * API istemcisi harcirah/ortak.jsx useHarcirahApi deseninin kopyası (harcirah'a bağımlılık olmasın diye).
 */

export const KOK = '/satis-sureci';

// getAuthHeaders her render'da yeni; istemci yalnız token değişince yenilenir (ortak/moduleApi gibi)
export function useSatisApi() {
  const { getAuthHeaders, token } = useAuth();
  return useMemo(() => {
    const cfg = () => ({ headers: getAuthHeaders() });
    const root = `${API_BASE_URL}${KOK}`;
    const veri = (r) => r.data.data;
    return {
      get: (yol, params = {}) => axios.get(`${root}${yol}`, { ...cfg(), params }).then(veri),
      post: (yol, govde) => axios.post(`${root}${yol}`, govde, cfg()).then(veri),
      put: (yol, govde) => axios.put(`${root}${yol}`, govde, cfg()).then(veri),
      del: (yol, params) => axios.delete(`${root}${yol}`, { ...cfg(), params }).then(veri),
      // multipart: Content-Type'ı tarayıcı (boundary ile) koyar
      form: (yol, formData) => axios.post(`${root}${yol}`, formData, {
        headers: { Authorization: getAuthHeaders().Authorization },
      }).then(veri),
      blob: (yol, params = {}) => axios.get(`${root}${yol}`, { ...cfg(), params, responseType: 'blob' }).then((r) => r.data),
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
}

export const hata = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d.map((x) => `${(x.loc || []).slice(-1)[0] || ''}: ${x.msg}`).join(' · ');
  return e?.message || 'İşlem başarısız.';
};

export const para = (v) => (v === null || v === undefined || v === ''
  ? '' : `${Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₺`);
export const tarih = (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '');
export const tarihSaat = (v) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '');
export const pazartesi = (d) => dayjs(d).startOf('day').subtract((dayjs(d).day() + 6) % 7, 'day');
export const GUNLER = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
export const gunAdi = (v) => GUNLER[(dayjs(v).day() + 6) % 7];
export const haftaEtiketi = (h) => `${dayjs(h).format('DD.MM')}–${dayjs(h).add(6, 'day').format('DD.MM.YYYY')}`;

/** InputNumber para biçimi (1.234.567,89) — utils/currency'ye bağımlı olmadan */
export const paraGirdisi = {
  formatter: (v) => (v === undefined || v === null || v === '' ? '' : `${v}`.replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')),
  parser: (v) => (v ? v.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '') : ''),
  min: 0,
  style: { width: '100%' },
};

/** Sözlük listesinden ({value,label}) etiket */
export const etiket = (liste, v) => (liste || []).find((x) => x.value === v)?.label;

// --- Fotoğraf (MinIO atmpoint/satis_gorusme/{id}/{ad}; görüntüleme poifotoUtils/imgproxy deseniyle) -----------
const FOTO_KOK = 'https://dosya.atmturkiye.com/atmpoint/satis_gorusme';
export const fotoUrl = (gorusmeId, ad, kucuk = true) => `${FOTO_KOK}/${gorusmeId}/${ad}${kucuk ? '/rs:fill:300:200/q:70' : ''}`;
export const fotoBuyukUrl = (gorusmeId, ad) => `${FOTO_KOK}/${gorusmeId}/${ad}/rs:fit:1600:1600/q:80`;
export const FOTO_MAKS = 10 * 1024 * 1024;
export const FOTO_MAKS_ADET = 20;

/** Fotoğrafı yüklemeden önce küçültür (uzun kenar 1600 px, JPEG %82; PHP resumkucult karşılığı).
 *  Desteklenmeyen tür ya da hata durumunda dosya aynen döner (sunucu içeriği ayrıca denetler). */
export async function fotoKucult(dosya) {
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(dosya.type)) return dosya;
  try {
    const bmp = await createImageBitmap(dosya);
    const oran = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
    if (oran === 1 && dosya.size < 1024 * 1024 && dosya.type === 'image/jpeg') return dosya;
    const tuval = document.createElement('canvas');
    tuval.width = Math.round(bmp.width * oran);
    tuval.height = Math.round(bmp.height * oran);
    tuval.getContext('2d').drawImage(bmp, 0, 0, tuval.width, tuval.height);
    const blob = await new Promise((ok) => tuval.toBlob(ok, 'image/jpeg', 0.82));
    return blob ? new File([blob], `${dosya.name.replace(/\.[^.]+$/, '')}.jpg`, { type: 'image/jpeg' }) : dosya;
  } catch {
    return dosya;
  }
}

/** Excel indirme (yetkili blob) */
export async function excelIndir(api, params, dosyaAdi) {
  const b = await api.blob('/disa-aktar', params);
  const u = URL.createObjectURL(b);
  const a = document.createElement('a');
  a.href = u;
  a.download = dosyaAdi;
  a.click();
  setTimeout(() => URL.revokeObjectURL(u), 60000);
}

// --- Küçük gösterim parçaları ------------------------------------------------------------------------------------
export const GORUSME_DURUM_RENK = { acik: 'processing', olumsuz: 'default', donusturuldu: 'success' };
export const PLAN_DURUM_RENK = { 0: 'processing', 1: 'success', 2: 'default' };

export function LokasyonLinki({ id, ad }) {
  if (!id) return null;
  return (
    <Tooltip title="Lokasyon detayı">
      <Link to={`/detay/${id}`}>{ad ? `${id} · ${ad}` : id}</Link>
    </Tooltip>
  );
}

export function DurumEtiketi({ durum, ad }) {
  return <Tag color={GORUSME_DURUM_RENK[durum] || 'default'} style={{ marginInlineEnd: 0 }}>{ad || durum}</Tag>;
}
